import { CityDTO } from "@/generated/@habacus/hcp_api/src";
import { fetchCities } from "@/utils/client/client_api";
import { Select } from "@habacuscode/habacus_ui_component_library";
import { useTranslations } from "next-intl";
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";

export interface ResidenceSelectorProps {
  id: string;
  name: string;
  required: boolean | undefined;
  label: string;
  defaultValue: string;
  locale: string;
  residence?: CityDTO;
  onChange: (e: any) => void;
}

export interface ResidenceSelectorRef {
  validate: () => boolean;
}

const ResidenceSelector = forwardRef<ResidenceSelectorRef, ResidenceSelectorProps>(({
  locale,
  id,
  name,
  required = false,
  label,
  defaultValue,
  residence,
  onChange,
}, ref) => {
  const tInput = useTranslations('Input');
  const tCommon = useTranslations('Common');
  const notInItaly: string = tInput("cities.notInItaly");
  const notInItalySearch: string[] = tInput("cities.notInItalySearch").toLowerCase().split(' ');

  const [cities, setCities] = useState<CityDTO[]>([]);
  const [residenceCity, setResidenceCity] = useState<CityDTO | null>(null);
  const [residenceCityPage, setResidenceCityPage] = useState<number>(1);
  const [residenceCityTerm, setResidenceCityTerm] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!residenceCity || !residenceCity.code) {
      newErrors["residence"] = tCommon("validation.error.valueMissing");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({ validate }));

  useEffect(() => {
    callGetCities("");
  }, []);

  useEffect(() => {
    if (residence) {
      const filtered = cities.filter(el => el.name!.toLowerCase().indexOf(residence.name!.toLowerCase()) !== -1);
      setCities(filtered.length > 0 ? filtered : [{ name: tCommon('noOptionsMessage'), code: undefined }]);
      setResidenceCity(residence);
    }
  }, [residence]);

  useEffect(() => {
    callGetCities(residenceCityTerm);
  }, [residenceCityPage, residenceCityTerm]);

  const callGetCities = async (filter: string = '') => {
    const res = await fetchCities({
      country: 'italia',
      page: residenceCityPage,
      perPage: 100,
      lang: locale,
      query: filter,
    });

    const data: CityDTO[] = res.response?.data || [];

    if (!filter
      || data.length == 0
      || notInItalySearch.some((word: string) => word.includes(filter.toLocaleLowerCase()))) {
      data.unshift({
        code: notInItaly,
        name: notInItaly,
      });
    }

    if (residenceCityPage === 1) {
      setCities(data);
    } else {
      setCities((prev) => [...prev, ...data]);
    }

    setLoading(false);
  };

  const onChangeCity = (city: CityDTO): void => {
    onChange({
      value: city,
    });
    setResidenceCity(city);
  };

  return (
    <div className="w-full" id={id}>
      <div className="space-y-4">
        <div className="flex flex-col">
          <Select
            id="cities"
            name="cities"
            key="search_cities"
            searchable={true}
            loading={loading}
            label={tInput("cities.label")}
            placeholder={tInput("cities.placeholder")}
            searchPlaceholder={tInput("cities.searchPlaceholder")}
            onSearch={(e: any) => {
              setResidenceCityPage(1);
              setResidenceCityTerm(e.toString());
            }}
            onScroll={() => {
              setResidenceCityPage((prevPage) => prevPage + 1);
            }}
            onChange={(city: CityDTO) => {
              onChangeCity(city);
              setErrors((prev) => ({ ...prev, residence: '' }));
            }}
            defaultValue={residence}
            selectFirstValue={false}
            items={cities}
            idKey="code"
            labelKey="name"
            required={true}
            error={errors['residence']}
          />
        </div>
      </div>
    </div>

  );
});

ResidenceSelector.displayName = "ResidenceSelector";
export default ResidenceSelector;
